/* eslint-disable import/no-extraneous-dependencies */
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, {
  useContext,
  useState,
  createContext,
  useEffect,
  ReactNode
} from 'react';

import { IdentityService } from '../services/IdentityService';
import jwtDecode from 'jwt-decode';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import { CognitoCredentialProvider } from '../api/CognitoCredentialProvider';
import {
  useErrorHandler,
  useInternalSession,
  useMember
} from './SessionProvider';
import { UserData } from '../api/backend/UserDataApi';
import { Information } from '../api/backend/NewsApi';
import { ConnectUserType } from '../constants';
import { enableNewrelicMonitoring } from '../utils/newrelic.utils';
import { Company } from '../models/company.model';

const IdentityServiceContext = createContext<IdentityService | undefined>(
  undefined
);
const UserDataContext = createContext<{
  userData?: UserData;
  updateUserData: (_: Partial<UserData>) => void;
}>(
  {} as { userData: UserData; updateUserData: (_: Partial<UserData>) => void }
);
const NewsContext = createContext<{
  news?: Information[];
  setNewsAsRead: (_: string) => void;
  getNews: () => void;
}>(
  {} as {
    informations?: Information[];
    setNewsAsRead: (_: string) => void;
    getNews: () => void;
  }
);

export const IdentityProvider = ({
  children
}: {
  children: ReactNode | undefined;
}) => {
  const { data } = useInternalSession('authenticated');
  const { member } = useMember();
  const { createProxy } = useErrorHandler()!;

  const [userData, setUserData] = useState<UserData>();
  const [news, setNews] = useState<Information[]>([]);

  const jwt = data?.idToken && jwtDecode(data.idToken);
  // 工務店かどうかのチェック
  const [identityService] = useState(() => {
    return createProxy(
      new IdentityService(
        ['kaiinAdmin', 'companyAdmin'].includes(jwt?.['custom:type']),
        new CognitoCredentialProvider(data.idToken!, data.accessToken!),
        process.env.NEXT_PUBLIC_S3_PROFILE_BUCKET
      )
    ) as IdentityService;
  });
  const isCognitoUser = data?.loginProvider === 'cognito';

  const updateUserData = (data: Partial<UserData>) => {
    identityService
      .updateUserData(data)
      .then((data) => setUserData({ ...data }));
  };

  const getNews = () => {
    if (isCognitoUser) {
      // アットホームから_お知らせ
      identityService.getNews().then((data) => setNews(data));
    }
  };

  const setNewsAsRead = (id: string) =>
    isCognitoUser
      ? identityService.setNewsAsRead(id).then((data) => setNews([...data]))
      : new Promise((resolve) => resolve({}));

  useEffect(() => {
    // ユーザデータ（既読、ピン留め情報）を取得する
    identityService.getUserData().then((data) => setUserData(data));

    // お知らせを取得する
    getNews();
  }, []);

  return (
    <NewsContext.Provider value={{ news, setNewsAsRead, getNews }}>
      <UserDataContext.Provider value={{ userData, updateUserData }}>
        <IdentityServiceContext.Provider value={identityService}>
          {children}
        </IdentityServiceContext.Provider>
      </UserDataContext.Provider>
    </NewsContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserDataContext);
}

export function useNews() {
  return useContext(NewsContext);
}

export function useIdentityService() {
  const context = useContext(IdentityServiceContext);
  const { data } = useInternalSession('authenticated');
  const { member } = useMember();

  if (context === undefined) {
    throw new Error(
      'useIdentityService must be used within a IdentityServiceContext'
    );
  }

  return {
    identityClient: context,
    credentialProvider: context._credentialProvider,
    useStaffList: (immutable?: boolean) =>
      (immutable ? useSWRImmutable : useSWR)(
        '/api/staffList',
        () =>
          data?.loginProvider === 'cognito' &&
          member.userType === ConnectUserType.Estate
            ? context.getStaffList()
            : [],
        {
          revalidateOnMount: true,
          refreshWhenHidden: true,
          revalidateIfStale: true
        }
      ),

    useCompany: (immutable?: boolean) => {
      const company = (immutable ? useSWRImmutable : useSWR)(
        '/api/company',
        () => {
          try {
            (window as any).isNewRelic = (window as any).isNewRelic
              ? (window as any).isNewRelic + 1
              : 1;
          } catch (e) {
            console.error(e);
          }

          return data?.loginProvider === 'cognito' &&
            member.userType === ConnectUserType.Estate
            ? context.getCompanyData()
            : undefined;
        }
      );

      // companyが取れた時の初回のみ実行
      try {
        if ((window as any)?.isNewRelic === 1) {
          (window as any).isNewRelic = 2;
          enableNewrelicMonitoring(company.data as Company);
        }
      } catch (e) {
        console.error(e);
      }

      return company;
    }
  };
}

export default IdentityProvider;
